import { NavLink } from 'react-router-dom';
/* eslint-disable jsx-a11y/label-has-associated-control */
import { useRef, useState, PointerEvent } from 'react';
import styled from 'styled-components';
import { PRICE_LIST_PERMISSION } from 'constants/price-list';
import { ADMIN_PERMISSION } from 'constants/permissions';
import { PRICE_REPORTS, priceReportRoute } from 'constants/price-reports';
import { useBackofficePermission } from 'hooks/useBackofficePermission';
import { useLocalStorage } from 'hooks/useLocalStorage';
import NavBarDropdown from './navBarDropdown';

// The menu can be dragged wider by its right edge, so long titles fit on one line; the width is kept
// in local storage, and a double click on the edge brings back the default.
const DEFAULT_WIDTH = 155;
const MIN_WIDTH = 120;
const MAX_WIDTH = 500;
const WIDTH_STORAGE_KEY = 'navBarWidth';

const clampWidth = (width: number) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width));

const Nav = styled.nav`
  position: relative;
  flex-shrink: 0;
  min-height: ${props => `calc(100vh - ${props.theme.headerHeight})`};
  height: 750.03px;
  background: #f2f2f2 0% 0% no-repeat padding-box;
  box-shadow: 0px 3px 6px #00000029;
  opacity: 1;
  text-align: left;
  display: flex;
  flex-direction: column;
  &:first-child {
    height: 3rem;
  }
`;

const StyledLink = styled(NavLink)`
  color: ${props => props.theme.colors.black};
  font-family: ${props => props.theme.fonts.semiBold};
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;
  margin: 5px 0 0 5px;
  padding-bottom: 3px;
  :hover {
    color: ${props => props.theme.colors.red};
  }
  &.active {
    border-bottom-color: white;
  }
  @media (max-width: 1200px) {
    font-size: 12px;
    margin: 5px 0 0 5px;
    font-family: ${props => props.theme.fonts.semiBold};
  }
`;

const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  right: -3px;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  touch-action: none;
  z-index: 1;
  :hover,
  &.dragging {
    background: #d6d9de;
  }
`;

const Separator = styled.div`
  height: 10px;
  width: 90%;
  margin-left: 5px;
  margin-bottom: 10px;
  border-bottom: 1px solid #d6d9de;
`;

export const NavBar = () => {
  const [savedWidth, saveWidth] = useLocalStorage(WIDTH_STORAGE_KEY, DEFAULT_WIDTH);
  const [width, setWidth] = useState<number>(() => clampWidth(Number(savedWidth) || DEFAULT_WIDTH));
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, width: 0 });

  const onResizeStart = (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStart.current = { x: e.clientX, width };
    setIsDragging(true);
  };
  const onResize = (e: PointerEvent<HTMLDivElement>) => {
    if (isDragging) {
      setWidth(clampWidth(dragStart.current.width + e.clientX - dragStart.current.x));
    }
  };
  const onResizeEnd = () => {
    if (isDragging) {
      setIsDragging(false);
      saveWidth(width);
    }
  };
  const onResizeReset = () => {
    setWidth(DEFAULT_WIDTH);
    saveWidth(DEFAULT_WIDTH);
  };

  const canManagePriceList = useBackofficePermission(PRICE_LIST_PERMISSION);
  const isAdmin = useBackofficePermission(ADMIN_PERMISSION);
  // The price list screens are off in production until the new flow replaces the legacy one. Price
  // List needs the price list permission; RAPI only needs the user to sign in. RapNet Price Lists shows
  // the lists the legacy site publishes, so it is on everywhere, for the price list permission.
  const pricesRoutes = [
    ...(CONFIG.PRICE_LIST_ENABLED && canManagePriceList
      ? [
          {
            id: 'PriceList',
            title: 'Price List',
            routes: [
              { route: '/priceupload', routeTitle: 'Price Upload' },
              { route: '/pricelist/currencyrates', routeTitle: 'Currency Rates' },
              { route: '/pricelist/parcels', routeTitle: 'Parcels' },
              { route: '/pricelist/texts', routeTitle: 'Weekly Texts' },
              { route: '/pricelist/preview', routeTitle: 'Preview' },
              { route: '/pricelist/templates', routeTitle: 'Templates' },
              { route: '/pricelist/publish', routeTitle: 'Publish' }
            ]
          }
        ]
      : []),
    ...(canManagePriceList
      ? [{ route: '/pricelist/rapnetlists', routeTitle: 'RapNet Price Lists' }]
      : []),
    ...(CONFIG.PRICE_LIST_ENABLED ? [{ route: '/pricelist/rapi', routeTitle: 'RAPI' }] : [])
  ];
  const Routes = [
    {
      id: 'Devices',
      title: 'Device Management',
      routes: [
        { route: '/devices', routeTitle: 'Devices' },
        { route: '/defaultsettings', routeTitle: 'Default Settings' },
        { route: '/usresettings', routeTitle: 'User Settings' }
      ]
    },
    {
      id: 'Upload',
      title: 'Upload',
      routes: [
        { route: '/servicemonitor', routeTitle: 'Service Monitor' },
        { route: '/uploadmonitor', routeTitle: 'Upload Monitor' },
        { route: '/certrefresh', routeTitle: 'Cert Refresh' },
        { route: '/gemstonecolumnmapping', routeTitle: 'Gemstone Column Mapping' },
        { route: '/gemstonevaluemapping', routeTitle: 'Gemstone Value Mapping' },
        { route: '/gemstonemanagevalidvalues', routeTitle: 'Gemstone Values Management' }
      ]
    },
    ...(pricesRoutes.length ? [{ id: 'Prices', title: 'Prices', routes: pricesRoutes }] : []),
    // The reports of the legacy OFC page Price Report Center spec A3, one menu item each.
    {
      id: 'PriceReports',
      title: 'Price Reports',
      routes: PRICE_REPORTS.map(report => ({
        route: priceReportRoute(report.slug),
        routeTitle: report.title
      }))
    },
    {
      id: 'Compliance',
      title: 'Compliance',
      routes: [
        { route: '/compliancecountryrequirements', routeTitle: 'Country Document Requirements' },
        { route: '/compliancedocumenttypes', routeTitle: 'Document Types' }
      ]
    },
    {
      id: 'SearchLogs',
      title: 'Search Logs',
      routes: [
        { route: '/searchlogs', routeTitle: 'Logs' },
        { route: '/searchlogs/defaultsettings', routeTitle: 'Default Settings' },
        { route: '/searchlogs/usersettings', routeTitle: 'User Settings' },
        { route: '/searchlogs/tierrules', routeTitle: 'Tier Limit Rules' },
        { route: '/searchlogs/tieroverrides', routeTitle: 'Account Overrides' },
        { route: '/searchlogs/tiertransitions', routeTitle: 'Tier Transitions' },
        { route: '/searchlogs/accounttiers', routeTitle: 'Account Tiers' },
        { route: '/searchlogs/eventlog', routeTitle: 'Event Log' }
      ]
    },
    // Deleted Diamonds and Diamond Summary need admin, as their APIs do.
    ...(isAdmin
      ? [
          {
            id: 'Diamonds',
            title: 'Diamonds',
            routes: [
              { route: '/deleteddiamonds', routeTitle: 'Deleted Diamonds' },
              { route: '/diamondsummary', routeTitle: 'Diamond Summary' }
            ]
          }
        ]
      : [])
  ];

  return (
    <Nav style={{ width }}>
      {Routes.map(route => (
        <NavBarDropdown routes={route.routes} id={route.id} title={route.title} />
      ))}
      <StyledLink to="/sourceverification">Source Verification</StyledLink>
      <Separator />
      <StyledLink to="/ringorders">Ring Orders</StyledLink>
      <ResizeHandle
        className={isDragging ? 'dragging' : ''}
        title="Drag to resize the menu, double click to reset"
        onPointerDown={onResizeStart}
        onPointerMove={onResize}
        onPointerUp={onResizeEnd}
        onPointerCancel={onResizeEnd}
        onDoubleClick={onResizeReset}
      />
    </Nav>
  );
};

export default NavBar;
